import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'

import { useLazyQuery, useMutation } from '@apollo/client'
import type { CRMObject } from 'types/graphql'

import { useParams } from '@redwoodjs/router'
import { useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import { logger } from 'src/lib/logger'

import {
  ADD_MESSAGE_TO_THREAD,
  GENERATE_ARTIFACT_AND_ADD_TO_THREAD,
  TITLE_THREAD,
  UPDATE_CONTEXT_OBJECTS,
} from '../../mutations'
import {
  GET_THREADS_QUERY,
  GET_CONTEXT_FOR_THREAD,
  GET_TIPTAP_JWT,
  GET_THREAD_WITH_CONTEXT,
} from '../../queries'

import { threadReducer } from './reducer'
import { adjustScrollLastMessage } from './scroll'
import ThreadContext, { ContextStatus, LoadingMessages } from './ThreadContext'

const maxTokens = 128000
// const ARTIFACT_CREATION_THRESHOLD = process.env.HOST.includes('localhost')
//   ? 10
//   : 2000

// Disable artifact creation for now
const ARTIFACT_CREATION_THRESHOLD = 1000000

const ThreadProvider = ({
  children,
  workspaceId,
  threadId = null,
}: {
  children: React.ReactNode
  workspaceId: string
  threadId?: string | null
}) => {
  const { currentUser } = useAuth()
  const { q: initialUserPrompt } = useParams()

  const isNew = threadId === 'new'

  const processedInitialPromptIds = useRef<string[]>([])

  const [state, dispatch] = useReducer(threadReducer, {
    workspaceId,
    id: threadId === 'new' ? null : threadId,
    thread: null,
    messages: [],
    loading: true,
    error: null,
    pages: [],
    contextObjects: [],
    contextStatus: null,
    pendingAiPrompt: null,
    pendingStreamingAiPrompt: null,
    loadingMessage: null,
    errorMessage: null,
    token: null,
    tokenLimit: maxTokens,
    tokenCount: 0,
  })

  const { data: _threadsData, refetch: refetchThreads } = useQuery(
    GET_THREADS_QUERY,
    {
      variables: { workspaceId },
      skip: !workspaceId,
      onCompleted: (data) => {
        dispatch({
          type: 'SET_THREADS',
          payload: data.threadsForUser,
        })
      },
      onError: (error) => {
        dispatch({
          type: 'SET_THREAD_ERROR',
          payload: error,
        })
      },
    }
  )

  const [titleThread] = useMutation(TITLE_THREAD)

  const handleTitleThread = useCallback(
    async (newTitle?: string) => {
      logger.dev('handleTitleThread', { newTitle })
      let title = newTitle

      const titleResult = await titleThread({
        variables: { threadId, workspaceId, title: newTitle },
      })

      if (!title) {
        title = titleResult.data.titleThread.title
      }

      dispatch({
        type: 'SET_THREAD_TITLE',
        payload: title,
      })
      await refetchThreads()
    },
    [titleThread, threadId, workspaceId, refetchThreads]
  )

  const { data: _threadData } = useQuery(GET_THREAD_WITH_CONTEXT, {
    variables: { threadId, workspaceId },
    skip: isNew || !threadId || !workspaceId,
    onCompleted: (data) => {
      logger.dev({ GET_THREAD_QUERY: data })
      dispatch({
        type: 'SET_THREAD',
        payload: data.threadWithContext.thread,
      })
      dispatch({
        type: 'SET_CONTEXT_STRING',
        payload: data.threadWithContext.context.contextString,
      })
      dispatch({
        type: 'SET_TOKEN_COUNT',
        payload: data.threadWithContext.context.tokenCount,
      })
      dispatch({
        type: 'SET_CONTEXT_STATUS',
        payload: ContextStatus.SUCCESS,
      })

      setTimeout(() => {
        adjustScrollLastMessage({
          behavior: 'smooth',
          block: 'start',
        })
      }, 200)

      if (!state?.thread?.title) {
        handleTitleThread()
      }
    },
    onError: (error) => {
      dispatch({
        type: 'SET_THREAD_ERROR',
        payload: error,
      })
    },
  })

  const jwtTimestamp = useMemo(() => {
    return Math.floor(Date.now() / 1000)
  }, [])

  const { data: _tiptapJwtData } = useQuery(GET_TIPTAP_JWT, {
    variables: { timestamp: jwtTimestamp },
    skip: !workspaceId,
    onCompleted: (data) => {
      logger.dev({ GET_TIPTAP_JWT: data })
      dispatch({
        type: 'SET_TOKEN',
        payload: data.getTiptapJwt,
      })
    },
  })

  const [fetchContext, { data: _contextData }] = useLazyQuery(
    GET_CONTEXT_FOR_THREAD
  )

  const [addMessageToThread, { data: _addMessageToThread }] = useMutation(
    ADD_MESSAGE_TO_THREAD
  )

  const [updateContextObjects, { data: _updateContextObjects }] = useMutation(
    UPDATE_CONTEXT_OBJECTS
  )

  const [generateArtifactAndAddToThread, { data: _artifactResult }] =
    useMutation(GENERATE_ARTIFACT_AND_ADD_TO_THREAD)

  const handleGenerateArtifactAndAddToThread = useCallback(
    async (threadMessageId: string) => {
      const artifactResult = await generateArtifactAndAddToThread({
        variables: { threadMessageId, workspaceId },
      })

      dispatch({
        type: 'ADD_MESSAGE',
        payload: artifactResult?.data?.generateArtifactAndAddToThread,
      })

      dispatch({
        type: 'SET_LOADING_MESSAGE',
        payload: null,
      })

      adjustScrollLastMessage({
        behavior: 'smooth',
        block: 'start',
        timeout: 200,
      })

      return artifactResult
    },
    [generateArtifactAndAddToThread, workspaceId]
  )

  const handleFetchContext = useCallback(async () => {
    if (threadId === 'new' && state?.thread) {
      return
    }

    const contextData = await fetchContext({
      variables: { threadId, workspaceId },
    })

    const contextString = contextData?.data?.contextForThread?.contextString

    if (!contextString) {
      dispatch({
        type: 'SET_CONTEXT_STATUS',
        payload: ContextStatus.ERROR_UNKNOWN,
      })
      return
    }

    if (contextString !== 'CONTEXT_OVERAGE') {
      dispatch({
        type: 'SET_CONTEXT_STRING',
        payload: contextString,
      })
      dispatch({
        type: 'SET_CONTEXT_STATUS',
        payload: ContextStatus.SUCCESS,
      })
      dispatch({
        type: 'SET_TOKEN_COUNT',
        payload: contextData.data.contextForThread.tokenCount,
      })

      return true
    } else {
      dispatch({
        type: 'SET_CONTEXT_STATUS',
        payload: ContextStatus.ERROR_CAPACITY,
      })

      return false
    }
  }, [fetchContext, threadId, workspaceId, state?.thread])

  const handleUpdateContextObjects = useCallback(
    async ({
      contextObjects,
      dismissedObjects,
    }: {
      contextObjects?: Partial<CRMObject>[]
      dismissedObjects?: Partial<CRMObject>[]
    }) => {
      logger.dev('handleUpdateContextObjects', {
        contextObjects,
        dismissedObjects,
      })
      if (Array.isArray(contextObjects)) {
        const cleanContextObjects = contextObjects
          .map((contextObject) => {
            return {
              objectId: contextObject.objectId,
              objectType: contextObject.objectType,
              workspaceId: contextObject.workspaceId,
            }
          })
          .filter(
            (contextObject) =>
              contextObject.objectId && contextObject.objectType
          )
        const cleanDismissedObjects = (dismissedObjects || [])
          .map((dismissedObject) => {
            return {
              objectId: dismissedObject.objectId,
              objectType: dismissedObject.objectType,
            }
          })
          .filter(
            (contextObject) =>
              contextObject.objectId && contextObject.objectType
          )

        if (
          cleanContextObjects.length > 0 ||
          cleanDismissedObjects.length > 0
        ) {
          const result = await updateContextObjects({
            variables: {
              threadId,
              workspaceId,
              contextObjects: cleanContextObjects,
              dismissedObjects: cleanDismissedObjects,
            },
          })
          logger.dev('updateContextObjects result', { result })
          await handleFetchContext()
        }
      }
    },
    [updateContextObjects, threadId, workspaceId, handleFetchContext]
  )

  const handleAiResponse = useCallback(
    async (result: any) => {
      const responseEditor = result.editor
      const html = responseEditor.getHTML()
      const json = responseEditor.getJSON()

      if (html === '<p></p>') {
        logger.warn('AI response is empty', {
          html,
          json,
          response: result?.response,
        })
        return
      }

      dispatch({
        type: 'ADD_MESSAGE',
        payload: {
          content: { html, json },
          type: 'html',
          userId: null,
          createdAt: new Date().toISOString(),
          id: Math.random().toString(36).substring(2, 15),
          links: [],
          pages: [],
        },
      })
      dispatch({
        type: 'SET_PENDING_AI_PROMPT',
        payload: null,
      })
      const newMessageResult = await addMessageToThread({
        variables: {
          threadId: state.thread.id,
          workspaceId,
          content: { html, json },
          type: 'html',
        },
      })
      await handleFetchContext()

      if (html?.length > ARTIFACT_CREATION_THRESHOLD) {
        dispatch({
          type: 'SET_LOADING_MESSAGE',
          payload: LoadingMessages.CREATING_PAGE,
        })
        await handleGenerateArtifactAndAddToThread(
          newMessageResult.data.addMessageToThread.id
        )
      }
    },
    [
      handleFetchContext,
      handleGenerateArtifactAndAddToThread,
      addMessageToThread,
      state?.thread?.id,
      workspaceId,
    ]
  )

  useEffect(() => {
    const handler = async () => {
      if (state?.thread?.messages?.length === 0) {
        logger.dev('Adding initial user prompt')
        dispatch({
          type: 'ADD_MESSAGE',
          payload: {
            content: { text: initialUserPrompt },
            userId: currentUser?.id,
            type: 'text',
            createdAt: new Date().toISOString(),
            id: Math.random().toString(36).substring(2, 15),
            links: [],
            pages: [],
          },
        })

        await addMessageToThread({
          variables: {
            workspaceId: state?.thread?.workspaceId,
            threadId: state?.thread?.id,
            content: { text: initialUserPrompt },
            userId: currentUser?.id,
            type: 'text',
            createdAt: new Date().toISOString(),
            id: Math.random().toString(36).substring(2, 15),
            links: [],
            pages: [],
          },
        })
      }

      if (!processedInitialPromptIds.current.includes(threadId)) {
        dispatch({
          type: 'SET_PENDING_AI_PROMPT',
          payload: initialUserPrompt,
        })
        processedInitialPromptIds.current.push(threadId)
      }
    }
    if (initialUserPrompt) {
      handler()
    }
  }, [
    initialUserPrompt,
    state?.thread?.messages?.length,
    currentUser?.id,
    addMessageToThread,
    state?.thread?.workspaceId,
    state?.thread?.id,
    processedInitialPromptIds,
    workspaceId,
    threadId,
  ])

  useEffect(() => {
    if (
      !initialUserPrompt &&
      state?.thread?.messages?.length === 1 &&
      state?.thread?.messages[0].userId &&
      !processedInitialPromptIds.current.includes(threadId)
    ) {
      dispatch({
        type: 'SET_PENDING_AI_PROMPT',
        payload: state?.thread?.messages[0]?.content?.text,
      })
      processedInitialPromptIds.current.push(threadId)
    }
  }, [
    initialUserPrompt,
    state?.thread?.messages,
    processedInitialPromptIds,
    workspaceId,
    threadId,
  ])

  const value = useMemo(
    () => ({
      dispatch,
      state,
      fetchContext: handleFetchContext,
      handleAiResponse,
      updateContextObjects: handleUpdateContextObjects,
      updateTitle: handleTitleThread,
    }),
    [
      dispatch,
      state,
      handleFetchContext,
      handleAiResponse,
      handleUpdateContextObjects,
      handleTitleThread,
    ]
  )

  return (
    <ThreadContext.Provider value={value}>{children}</ThreadContext.Provider>
  )
}

export default ThreadProvider
